/**
 * Project Management Service
 * Handles all project management related API calls
 */

import { httpClient, ApiResponse } from './http.client';

// =================== INTERFACES ===================

export interface Project {
  project_id: string;
  organization_id: string;
  project_name: string;
  project_number?: string;
  description?: string;
  project_type?: 'residential' | 'commercial' | 'industrial' | 'infrastructure';
  status: 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'critical';
  
  // Location
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  
  // People
  project_manager_id?: string;
  client_name?: string;
  client_email?: string;
  client_phone?: string;
  
  // Timeline
  start_date?: string;
  end_date?: string;
  actual_start_date?: string;
  actual_end_date?: string;
  
  // Budget
  total_budget?: number;
  spent_amount?: number;
  remaining_budget?: number;
  completion_percentage?: number;
  
  // Metadata
  created_by: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

export interface Schedule {
  schedule_id: string;
  project_id: string;
  task_name: string;
  description?: string;
  start_date: string;
  end_date: string;
  duration_days?: number;
  predecessor_task_id?: string;
  dependency_type?: string;
  assigned_to?: string;
  estimated_hours?: number;
  actual_hours?: number;
  status: 'not_started' | 'in_progress' | 'completed' | 'on_hold' | 'cancelled';
  completion_percentage: number;
  priority: 'low' | 'medium' | 'high' | 'critical';
  created_at: string;
  updated_at: string;
}

export interface DailyLog {
  log_id: string;
  project_id: string;
  log_date: string;
  weather_conditions?: string;
  temperature_high?: number;
  temperature_low?: number;
  work_performed?: string;
  crew_count?: number;
  hours_worked?: number;
  delays?: string;
  safety_observations?: string;
  quality_issues?: string;
  notes?: string;
  materials_delivered?: any[];
  equipment_on_site?: string[];
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface Document {
  document_id: string;
  project_id: string;
  document_name: string;
  document_type: 'contract' | 'drawing' | 'specification' | 'permit' | 'invoice' | 'photo' | 'report' | 'other';
  description?: string;
  file_path: string;
  file_size?: number;
  mime_type?: string;
  file_extension?: string;
  version: string;
  is_latest: boolean;
  previous_version_id?: string;
  is_public: boolean;
  uploaded_by: string;
  tags?: string[];
  created_at: string;
  updated_at: string;
}

export interface RFI {
  rfi_id: string;
  project_id: string;
  rfi_number: string;
  title: string;
  description: string;
  submitted_by: string;
  assigned_to?: string;
  status: 'open' | 'pending_response' | 'answered' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  due_date?: string;
  response?: string;
  responded_by?: string;
  responded_at?: string;
  drawing_references?: string[];
  specification_references?: string[];
  created_at: string;
  updated_at: string;
}

export interface Inspection {
  inspection_id: string;
  project_id: string;
  inspection_type: string;
  title: string;
  description?: string;
  scheduled_date: string;
  actual_date?: string;
  inspector_name?: string;
  inspector_contact?: string;
  status: 'scheduled' | 'in_progress' | 'passed' | 'failed' | 'rescheduled';
  result?: string;
  pass_fail?: boolean;
  required_actions?: string;
  reinspection_date?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface SafetyIncident {
  incident_id: string;
  project_id: string;
  incident_number: string;
  incident_date: string;
  location: string;
  description: string;
  severity: 'minor' | 'moderate' | 'major' | 'critical';
  injured_person_name?: string;
  injured_person_company?: string;
  witness_names?: string[];
  immediate_action_taken?: string;
  medical_attention_required: boolean;
  hospital_name?: string;
  root_cause?: string;
  corrective_actions?: string;
  preventive_measures?: string;
  reported_by: string;
  reported_to_authorities: boolean;
  authority_report_number?: string;
  investigation_complete: boolean;
  closed_date?: string;
  created_at: string;
  updated_at: string;
}

export interface PunchList {
  punch_item_id: string;
  project_id: string;
  item_number: string;
  description: string;
  location: string;
  trade?: string;
  category?: string;
  assigned_to?: string;
  contractor_company?: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'on_hold' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'critical';
  due_date?: string;
  completed_date?: string;
  work_description?: string;
  completion_notes?: string;
  inspected_by?: string;
  inspection_date?: string;
  approved?: boolean;
  identified_by: string;
  created_at: string;
  updated_at: string;
}

// ─── Project Locations ────────────────────────────────────────────────────────

export interface ProjectLocation {
  location_id: string;
  project_id: string;
  location_name: string;
  location_type: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
  coordinates: { lat: number; lng: number } | null;
  start_date: string | null;
  end_date: string | null;
  notes: string | null;
  created_at: string;
}

export interface ProjectLocationCreate {
  location_name: string;
  location_type?: string;
  address?: string;
  city?: string;
  country?: string;
  coordinates?: { lat: number; lng: number } | null;
  start_date?: string;
  end_date?: string;
  notes?: string;
}

// ─── Project Team Members ─────────────────────────────────────────────────────

export type ApprovalModule =
  | 'purchase_orders'
  | 'invoices'
  | 'change_orders'
  | 'rfis'
  | 'contracts'
  | 'budgets';

export interface ProjectTeamMember {
  membership_id: string;
  project_id: string;
  user_id: string;
  full_name: string;
  email: string;
  project_role: string;
  approval_modules: ApprovalModule[];
  approval_limit: number | null;
  is_active: boolean;
  joined_date: string | null;
  left_date: string | null;
  created_at: string;
}

export interface ProjectTeamMemberCreate {
  user_id: string;
  project_role: string;
  approval_modules?: ApprovalModule[];
  approval_limit?: number;
  joined_date?: string;
}

export interface ProjectTeamMemberUpdate {
  project_role?: string;
  approval_modules?: ApprovalModule[];
  approval_limit?: number | null;
}

// =================== PROJECT SERVICE CLASS ===================

class ProjectService {
  // =================== PROJECTS ===================
  
  async getProjects(page = 1, perPage = 10, filters?: any): Promise<ApiResponse<{ data: Project[]; total: number; page: number; per_page: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      ...filters
    });
    
    return httpClient.get<{ data: Project[]; total: number; page: number; per_page: number }>(
      `/api/v1/projects/?${queryParams}`
    );
  }

  async getProject(projectId: string): Promise<ApiResponse<Project>> {
    return httpClient.get<Project>(`/api/v1/projects/${projectId}`);
  }

  async createProject(projectData: Partial<Project>): Promise<ApiResponse<Project>> {
    return httpClient.post<Project>('/api/v1/projects/', projectData);
  }

  async updateProject(projectId: string, projectData: Partial<Project>): Promise<ApiResponse<Project>> {
    return httpClient.put<Project>(`/api/v1/projects/${projectId}`, projectData);
  }

  async deleteProject(projectId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/projects/${projectId}`);
  }

  // =================== SCHEDULES ===================

  async getSchedules(projectId?: string, page = 1, perPage = 50): Promise<ApiResponse<{ data: Schedule[]; total: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    
    if (projectId) {
      queryParams.append('project_id', projectId);
    }

    return httpClient.get<{ data: Schedule[]; total: number }>(
      `/api/v1/project-management/schedules?${queryParams}`
    );
  }

  async createScheduleTask(taskData: Partial<Schedule>): Promise<ApiResponse<Schedule>> {
    return httpClient.post<Schedule>('/api/v1/project-management/schedules', taskData);
  }

  async updateScheduleTask(scheduleId: string, taskData: Partial<Schedule>): Promise<ApiResponse<Schedule>> {
    return httpClient.put<Schedule>(`/api/v1/project-management/schedules/${scheduleId}`, taskData);
  }

  async deleteScheduleTask(scheduleId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/project-management/schedules/${scheduleId}`);
  }

  // =================== DAILY LOGS ===================

  async getDailyLogs(projectId?: string, page = 1, perPage = 20): Promise<ApiResponse<{ data: DailyLog[]; total: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    
    if (projectId) {
      queryParams.append('project_id', projectId);
    }

    return httpClient.get<{ data: DailyLog[]; total: number }>(
      `/api/v1/project-management/daily-logs?${queryParams}`
    );
  }

  async createDailyLog(logData: Partial<DailyLog>): Promise<ApiResponse<DailyLog>> {
    return httpClient.post<DailyLog>('/api/v1/project-management/daily-logs', logData);
  }

  async updateDailyLog(logId: string, logData: Partial<DailyLog>): Promise<ApiResponse<DailyLog>> {
    return httpClient.put<DailyLog>(`/api/v1/project-management/daily-logs/${logId}`, logData);
  }

  async deleteDailyLog(logId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/project-management/daily-logs/${logId}`);
  }

  // =================== DOCUMENTS ===================

  async getDocuments(projectId?: string, page = 1, perPage = 20): Promise<ApiResponse<{ data: Document[]; total: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    
    if (projectId) {
      queryParams.append('project_id', projectId);
    }

    return httpClient.get<{ data: Document[]; total: number }>(
      `/api/v1/project-management/documents?${queryParams}`
    );
  }

  async uploadDocument(documentData: FormData): Promise<ApiResponse<Document>> {
    return httpClient.post<Document>('/api/v1/project-management/documents/upload', documentData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  async updateDocument(documentId: string, documentData: Partial<Document>): Promise<ApiResponse<Document>> {
    return httpClient.put<Document>(`/api/v1/project-management/documents/${documentId}`, documentData);
  }

  async deleteDocument(documentId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/project-management/documents/${documentId}`);
  }

  // =================== RFIs ===================

  async getRFIs(projectId?: string, page = 1, perPage = 20): Promise<ApiResponse<{ data: RFI[]; total: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    
    if (projectId) {
      queryParams.append('project_id', projectId);
    }

    return httpClient.get<{ data: RFI[]; total: number }>(
      `/api/v1/project-management/rfis?${queryParams}`
    );
  }

  async createRFI(rfiData: Partial<RFI>): Promise<ApiResponse<RFI>> {
    return httpClient.post<RFI>('/api/v1/project-management/rfis', rfiData);
  }

  async updateRFI(rfiId: string, rfiData: Partial<RFI>): Promise<ApiResponse<RFI>> {
    return httpClient.put<RFI>(`/api/v1/project-management/rfis/${rfiId}`, rfiData);
  }

  async deleteRFI(rfiId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/project-management/rfis/${rfiId}`);
  }

  async respondToRFI(rfiId: string, response: string): Promise<ApiResponse<RFI>> {
    return httpClient.post<RFI>(`/api/v1/project-management/rfis/${rfiId}/respond`, { response });
  }

  // =================== INSPECTIONS ===================

  async getInspections(projectId?: string, page = 1, perPage = 20): Promise<ApiResponse<{ data: Inspection[]; total: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    
    if (projectId) {
      queryParams.append('project_id', projectId);
    }

    return httpClient.get<{ data: Inspection[]; total: number }>(
      `/api/v1/project-management/inspections?${queryParams}`
    );
  }

  async createInspection(inspectionData: Partial<Inspection>): Promise<ApiResponse<Inspection>> {
    return httpClient.post<Inspection>('/api/v1/project-management/inspections', inspectionData);
  }

  async updateInspection(inspectionId: string, inspectionData: Partial<Inspection>): Promise<ApiResponse<Inspection>> {
    return httpClient.put<Inspection>(`/api/v1/project-management/inspections/${inspectionId}`, inspectionData);
  }

  async deleteInspection(inspectionId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/project-management/inspections/${inspectionId}`);
  }

  async completeInspection(inspectionId: string, completionData: any): Promise<ApiResponse<Inspection>> {
    return httpClient.post<Inspection>(`/api/v1/project-management/inspections/${inspectionId}/complete`, completionData);
  }

  // =================== SAFETY INCIDENTS ===================

  async getSafetyIncidents(projectId?: string, page = 1, perPage = 20): Promise<ApiResponse<{ data: SafetyIncident[]; total: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    
    if (projectId) {
      queryParams.append('project_id', projectId);
    }

    return httpClient.get<{ data: SafetyIncident[]; total: number }>(
      `/api/v1/project-management/safety-incidents?${queryParams}`
    );
  }

  async createSafetyIncident(incidentData: Partial<SafetyIncident>): Promise<ApiResponse<SafetyIncident>> {
    return httpClient.post<SafetyIncident>('/api/v1/project-management/safety-incidents', incidentData);
  }

  async updateSafetyIncident(incidentId: string, incidentData: Partial<SafetyIncident>): Promise<ApiResponse<SafetyIncident>> {
    return httpClient.put<SafetyIncident>(`/api/v1/project-management/safety-incidents/${incidentId}`, incidentData);
  }

  async deleteSafetyIncident(incidentId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/project-management/safety-incidents/${incidentId}`);
  }

  async completeInvestigation(incidentId: string, investigationData: any): Promise<ApiResponse<SafetyIncident>> {
    return httpClient.post<SafetyIncident>(`/api/v1/project-management/safety-incidents/${incidentId}/complete-investigation`, investigationData);
  }

  async getSafetyStats(): Promise<ApiResponse<any>> {
    return httpClient.get<any>('/api/v1/project-management/safety-incidents/stats/summary');
  }

  // =================== PUNCH LISTS ===================

  async getPunchListItems(projectId?: string, page = 1, perPage = 50): Promise<ApiResponse<{ data: PunchList[]; total: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    
    if (projectId) {
      queryParams.append('project_id', projectId);
    }

    return httpClient.get<{ data: PunchList[]; total: number }>(
      `/api/v1/project-management/punch-lists?${queryParams}`
    );
  }

  async createPunchListItem(punchData: Partial<PunchList>): Promise<ApiResponse<PunchList>> {
    return httpClient.post<PunchList>('/api/v1/project-management/punch-lists', punchData);
  }

  async updatePunchListItem(punchId: string, punchData: Partial<PunchList>): Promise<ApiResponse<PunchList>> {
    return httpClient.put<PunchList>(`/api/v1/project-management/punch-lists/${punchId}`, punchData);
  }

  async deletePunchListItem(punchId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/project-management/punch-lists/${punchId}`);
  }

  async completePunchItem(punchItemId: string, completionData: any): Promise<ApiResponse<PunchList>> {
    return httpClient.post<PunchList>(`/api/v1/project-management/punch-lists/${punchItemId}/complete`, completionData);
  }

  async inspectPunchItem(punchItemId: string, inspectionData: any): Promise<ApiResponse<PunchList>> {
    return httpClient.post<PunchList>(`/api/v1/project-management/punch-lists/${punchItemId}/inspect`, inspectionData);
  }

  async getPunchListStats(): Promise<ApiResponse<any>> {
    return httpClient.get<any>('/api/v1/project-management/punch-lists/stats/summary');
  }

  // =================== UTILITY METHODS ===================

  /**
   * Get project with details
   */
  async getProjectDetails(projectId: string): Promise<ApiResponse<any>> {
    return httpClient.get<any>(`/api/v1/projects/${projectId}/details`);
  }

  /**
   * Get project statistics
   */
  async getProjectStats(projectId: string): Promise<ApiResponse<any>> {
    return httpClient.get<any>(`/api/v1/projects/${projectId}/stats`);
  }

  /**
   * Duplicate project
   */
  async duplicateProject(projectId: string, newProjectData?: any): Promise<ApiResponse<Project>> {
    return httpClient.post<Project>(`/api/v1/projects/${projectId}/duplicate`, newProjectData || {});
  }

  /**
   * Archive project
   */
  async archiveProject(projectId: string): Promise<ApiResponse<void>> {
    return httpClient.post<void>(`/api/v1/projects/${projectId}/archive`, {});
  }

  /**
   * Get project status options
   */
  async getProjectStatusOptions(): Promise<ApiResponse<string[]>> {
    return httpClient.get<string[]>('/api/v1/projects/status/options');
  }

  // =================== PROJECT TASKS ===================

  /**
   * Get project tasks
   */
  async getProjectTasks(projectId?: string, page = 1, perPage = 50): Promise<ApiResponse<{ data: any[]; total: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    
    if (projectId) {
      queryParams.append('project_id', projectId);
    }

    return httpClient.get<{ data: any[]; total: number }>(
      `/api/v1/project-management/project-tasks?${queryParams}`
    );
  }

  /**
   * Create project task
   */
  async createProjectTask(taskData: any): Promise<ApiResponse<any>> {
    return httpClient.post<any>('/api/v1/project-management/project-tasks', taskData);
  }

  /**
   * Update project task
   */
  async updateProjectTask(taskId: string, taskData: any): Promise<ApiResponse<any>> {
    return httpClient.put<any>(`/api/v1/project-management/project-tasks/${taskId}`, taskData);
  }

  /**
   * Delete project task
   */
  async deleteProjectTask(taskId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/project-management/project-tasks/${taskId}`);
  }

  /**
   * Get task dependencies
   */
  async getTaskDependencies(taskId: string): Promise<ApiResponse<any[]>> {
    return httpClient.get<any[]>(`/api/v1/project-management/project-tasks/${taskId}/dependencies`);
  }

  /**
   * Update task status
   */
  async updateTaskStatus(taskId: string, status: string): Promise<ApiResponse<any>> {
    return httpClient.put<any>(`/api/v1/project-management/project-tasks/${taskId}/status`, { status });
  }

  /**
   * Get project critical path
   */
  async getProjectCriticalPath(projectId: string): Promise<ApiResponse<any[]>> {
    return httpClient.get<any[]>(`/api/v1/project-management/project-tasks/project/${projectId}/critical-path`);
  }

  // =================== PROJECT LOCATIONS ===================

  async getProjectLocations(projectId: string): Promise<ProjectLocation[]> {
    const res = await httpClient.get<ProjectLocation[]>(`/api/v1/projects/${projectId}/locations`);
    return Array.isArray((res as any).data) ? (res as any).data : Array.isArray(res) ? res : [];
  }

  async createProjectLocation(projectId: string, data: ProjectLocationCreate): Promise<ProjectLocation> {
    const res = await httpClient.post<ProjectLocation>(`/api/v1/projects/${projectId}/locations`, data);
    return (res as any).data ?? res;
  }

  async updateProjectLocation(projectId: string, locationId: string, data: Partial<ProjectLocationCreate>): Promise<ProjectLocation> {
    const res = await httpClient.put<ProjectLocation>(`/api/v1/projects/${projectId}/locations/${locationId}`, data);
    return (res as any).data ?? res;
  }

  async deleteProjectLocation(projectId: string, locationId: string): Promise<void> {
    await httpClient.delete(`/api/v1/projects/${projectId}/locations/${locationId}`);
  }

  // =================== PROJECT TEAM MEMBERS ===================

  async getProjectTeamMembers(projectId: string): Promise<ProjectTeamMember[]> {
    const res = await httpClient.get<ProjectTeamMember[]>(`/api/v1/projects/${projectId}/members`);
    return Array.isArray((res as any).data) ? (res as any).data : Array.isArray(res) ? res : [];
  }

  async addProjectTeamMember(projectId: string, data: ProjectTeamMemberCreate): Promise<ProjectTeamMember> {
    const res = await httpClient.post<ProjectTeamMember>(`/api/v1/projects/${projectId}/members`, data);
    return (res as any).data ?? res;
  }

  async updateProjectTeamMember(projectId: string, membershipId: string, data: ProjectTeamMemberUpdate): Promise<ProjectTeamMember> {
    const res = await httpClient.put<ProjectTeamMember>(`/api/v1/projects/${projectId}/members/${membershipId}`, data);
    return (res as any).data ?? res;
  }

  async removeProjectTeamMember(projectId: string, membershipId: string): Promise<void> {
    await httpClient.delete(`/api/v1/projects/${projectId}/members/${membershipId}`);
  }
}

// Export singleton instance
export const projectService = new ProjectService();